import { useEffect, useState, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";


export type EmailStatus =  "draft" | "sent" | "replied";

export type Contact = {
  id: string;
  name: string;
  role: string;
  email?: string;
  linkedin?: string;
  website?: string;
  status: EmailStatus;
  emailContent?: string;
  sentDate?: string;
  archived: boolean;
};

export default function AddContactModal({ 
  isOpen, 
  onClose, 
  onAdd,
  editContact
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onAdd: (contact: Omit<Contact, "id" | "createdAt">) => void;
  editContact?: Contact;
}) {
  const [name, setName] = useState(editContact?.name || "");
  const [role, setRole] = useState(editContact?.role || "");
  const [email, setEmail] = useState(editContact?.email || "");
  const [linkedin, setLinkedin] = useState(editContact?.linkedin || "");
  const [website, setWebsite] = useState(editContact?.website || "");

  useEffect(() => {
    if (editContact) {
      setName(editContact.name);
      setRole(editContact.role);
      setEmail(editContact.email || "");
      setLinkedin(editContact.linkedin || "");
      setWebsite(editContact.website || "");
    }
  }, [editContact]);

  const handleSubmit = () => {
    if (!name.trim() || !role.trim()) return;

    onAdd({
      name: name.trim(),
      role: role.trim(),
      email: email.trim() || '',
      linkedin: linkedin.trim() || '',
      website: website.trim() || '',
      status: editContact?.status || "draft",
      emailContent: editContact?.emailContent || "",
      sentDate: editContact?.sentDate,
      archived: editContact?.archived || false,
    });

    if (!editContact) {
      setName("");
      setRole("");
      setEmail("");
      setLinkedin("");
      setWebsite("");
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="block max-w-md p-0 shadow-2xl">
        <div className="border-b  border-cocoa/20 px-6 py-4">
          <DialogTitle className="text-xl">
            {editContact ? 'Edit Contact' : 'Add New Contact'}
          </DialogTitle>
          <DialogDescription className="mt-1">Add someone to reach out to</DialogDescription>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., John Doe"
              className="w-full px-4 py-3 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Role *
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g., Senior Software Engineer"
              className="w-full px-4 py-3 rounded-lg border  border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@company.com"
              className="w-full px-4 py-3 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              LinkedIn URL
            </label>
            <input
              type="url"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="linkedin.com/in/johndoe"
              className="w-full px-4 py-3 rounded-lg border  border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso mb-2">
              Website
            </label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="portfolio.com"
              className="w-full px-4 py-3 rounded-lg border border-cocoa/40 text-espresso focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition placeholder:text-cocoa/40"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!name.trim() || !role.trim()}
              className="px-6 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition disabled:opacity-50"
            >
              {editContact ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}